import { useMutation, type ApolloError } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  NOTIFY_BACK_IN_STOCK,
  type INotifyBackInStock,
  type INotifyBackInStockVariables,
} from '@commerce/core'

export interface UseBackInStockNotice {
  /** Ask to be told when this sku returns. */
  notifyMe: () => void
  /** True once the server has taken the request, for this sku, on this visit. */
  requested: boolean
  loading: boolean
  error: ApolloError | undefined
}

/**
 * "Avísame cuando llegue", for a product that is out of stock.
 *
 * Until now an out-of-stock product was a dead end: the card's add button is
 * disabled and the detail page's button said "Sin stock" and did nothing, so
 * the only way to find out it came back was to come and look.
 *
 * **The confirmation lasts as long as the page does.** Nothing in the schema
 * records that a shopper asked — not on `Product`, not on the session — so what
 * is held here is state, not knowledge: a reload shows the button again, and a
 * second tap is a second request the server can dedupe or not. That is a real
 * limitation and it is here rather than in the component, because a component
 * that kept a "we told you" flag would be claiming to know something it does
 * not.
 */
export const useBackInStockNotice = (sku: string): UseBackInStockNotice => {
  const { slug = '', sessionId = '' } = useParams()

  /**
   * *Which* sku was confirmed, not merely that one was.
   *
   * A boolean cannot survive the page changing product under it — and it does,
   * because a related product is one tap away. Tap the notice, tap an
   * out-of-stock related product before the mutation answers, and the reply for
   * the product the shopper *left* would light up the product they are now
   * looking at: a promise nobody made, about a sku nobody asked about.
   */
  const [requestedSku, setRequestedSku] = useState<string | null>(null)

  const [mutate, { loading, error, reset }] = useMutation<
    INotifyBackInStock,
    INotifyBackInStockVariables
  >(NOTIFY_BACK_IN_STOCK, { onError: () => undefined })

  // A failure belongs to the product it happened on. Without this, one product
  // that could not be subscribed to leaves its alert sitting on the next one.
  useEffect(() => {
    reset()
  }, [sku, reset])

  const notifyMe = useCallback(() => {
    if (sku === '') return
    // Captured, so the reply is attributed to the sku that was asked about
    // rather than to whatever `sku` has become by the time it lands.
    const asked = sku
    void mutate({ variables: { storefrontName: slug, sessionUid: sessionId, sku: asked } }).then(
      (result) => {
        // A resolved promise is not a yes: `onError` above swallows the
        // rejection, and the server declines an unknown sku by answering null.
        if (result?.data?.notifyBackInStock) setRequestedSku(asked)
      },
    )
  }, [mutate, slug, sessionId, sku])

  return {
    notifyMe,
    requested: sku !== '' && requestedSku === sku,
    loading,
    error,
  }
}
